<template>
  <div
    v-if="eventPageStore.isLoading"
    class="fixed inset-0 z-[10000] flex items-center justify-center bg-black"
  >
    <div class="text-center">
      <i class="pi pi-spin pi-spinner text-4xl cs-spinner"></i>
      <p class="mt-4 text-gray-400">{{ t("event.loading") }}</p>
    </div>
  </div>

  <div v-else class="led-root flex h-screen overflow-hidden bg-gray-950 text-white">
    <!-- ══════════════════════════════════════════
         LEFT — Bar previews
         ══════════════════════════════════════════ -->
    <div class="flex-1 overflow-x-auto overflow-y-hidden flex items-start min-w-0">
      <!-- Error -->
      <div v-if="error" class="flex items-center justify-center w-full h-full">
        <div class="text-center">
          <i class="pi pi-exclamation-triangle text-4xl text-red-400 mb-4"></i>
          <p class="text-red-400 text-lg">{{ error }}</p>
        </div>
      </div>

      <!-- Bars -->
      <template v-else>
        <div
          v-for="bar in bars"
          :key="bar.id"
          class="led-bar flex-shrink-0 flex flex-col h-screen"
          :class="bar.theme === 'light' ? 'led-bar--light' : 'led-bar--dark'"
          :style="{ width: bar.width + 'px' }"
        >
          <!-- ── Logo (always shown) ── -->
          <div class="led-section led-logo flex items-center justify-center py-3 px-2">
            <img
              :src="concertsStyleStore.logoSrc(bar.theme === 'dark')"
              alt="Jukesoft"
              class="max-h-10 object-contain"
            />
          </div>

          <!-- ── Event name ── -->
          <div
            v-if="bar.sections.eventName && event"
            class="led-section led-event-name relative overflow-hidden"
          >
            <div
              v-if="!concertsStyleStore.isWise"
              class="absolute inset-0 bg-no-repeat bg-cover bg-center"
              :style="
                bar.theme === 'light'
                  ? 'background-image: url(\'/background/jbc-bg-light-stripe.webp\'); background-position-y: 0%'
                  : 'background-image: url(\'/background/jb-bg-dark-stripe.webp\'); background-position-y: 100%'
              "
            ></div>
            <div
              v-else
              class="absolute inset-0"
              :class="
                bar.theme === 'light'
                  ? 'cs-led-header-gradient-light'
                  : 'cs-led-header-gradient-dark'
              "
            ></div>
            <!-- Text overlay -->
            <div class="relative px-3 py-4 text-center">
              <span
                class="text-md font-extrabold leading-tight break-words block"
                :class="bar.theme === 'light' ? 'text-gray-900' : 'text-white'"
                >{{ event.name }}</span
              >
            </div>
          </div>

          <!-- ── QR code ── -->
          <div
            v-if="bar.sections.qrCode && qrCodeDataUrl"
            class="led-section led-qr flex flex-col items-center py-4 px-3"
            :class="
              bar.theme === 'light'
                ? 'bg-gray-50 border-t border-gray-200'
                : 'bg-gray-950 border-t border-gray-800'
            "
          >
            <!-- Bordered card wrapping label + QR -->
            <div
              class="w-full flex flex-col items-center rounded-xl p-3 gap-3"
              :class="
                bar.theme === 'light'
                  ? 'bg-white border border-gray-200 shadow-sm'
                  : 'bg-gray-900 border border-gray-700 shadow-sm'
              "
            >
              <p
                class="text-sm font-semibold text-center leading-snug"
                :class="bar.theme === 'light' ? 'text-gray-900' : 'text-white'"
              >
                {{ tForLocale(bar.language, isKaraoke ? "event.browseSongs" : "event.joinVoting") }}
              </p>
              <div
                class="rounded-lg p-2 bg-white"
                :class="bar.theme === 'light' ? 'border border-gray-200' : 'border border-gray-300'"
              >
                <img
                  :src="qrCodeDataUrl"
                  :alt="tForLocale(bar.language, 'event.qrCodeAlt')"
                  style="max-width: 250px; width: 100%"
                />
              </div>
            </div>
          </div>

          <!-- ── Currently Playing ── -->
          <Transition name="led-section">
            <div
              v-if="bar.sections.currentlyPlaying && latestPerformedSong && !isKaraoke"
              class="led-section led-now-playing px-2 py-4"
              :class="
                bar.theme === 'light'
                  ? 'bg-white border-t border-gray-200'
                  : 'bg-gray-950 border-t border-gray-800'
              "
            >
              <div
                class="rounded-xl border-2 px-3 py-2"
                :class="
                  bar.theme === 'light'
                    ? 'border-green-400 bg-green-50/50'
                    : 'border-green-500 bg-green-900/20'
                "
              >
                <span
                  class="text-sm uppercase tracking-widest font-semibold mb-1 block"
                  :class="bar.theme === 'light' ? 'text-green-700' : 'text-green-400'"
                  >{{ tForLocale(bar.language, "event.currentlyPlayingBadge") }}</span
                >
                <p
                  class="text-[20px] font-family-oswald font-bold leading-tight break-words text-sm"
                  :class="bar.theme === 'light' ? 'text-gray-900' : 'text-white'"
                >
                  {{ latestPerformedSong.title }}
                </p>
                <p
                  v-if="latestPerformedSong.author"
                  class="text-xs mt-0.5 opacity-70 break-words"
                  :class="bar.theme === 'light' ? 'text-gray-700' : 'text-gray-300'"
                >
                  {{ latestPerformedSong.author }}
                </p>
              </div>
            </div>
          </Transition>

          <!-- ── Up Next ── -->
          <Transition name="led-section">
            <div
              v-if="bar.sections.upNext && upNextSong && !isKaraoke"
              class="led-section led-up-next px-2 py-4"
              :class="
                bar.theme === 'light'
                  ? 'bg-white border-t border-gray-200'
                  : 'bg-gray-950 border-t border-gray-800'
              "
            >
              <div
                class="rounded-xl border-2 px-3 py-2"
                :class="
                  bar.theme === 'light'
                    ? 'border-amber-400 bg-amber-50/50'
                    : 'border-amber-500 bg-amber-900/20'
                "
              >
                <span
                  class="text-sm uppercase tracking-widest font-semibold mb-1 block"
                  :class="bar.theme === 'light' ? 'text-amber-700' : 'text-amber-400'"
                  >{{ tForLocale(bar.language, "event.upNext") }}</span
                >
                <p
                  class="font-bold leading-tight break-words text-sm"
                  :class="bar.theme === 'light' ? 'text-gray-900' : 'text-white'"
                >
                  {{ upNextSong.title }}
                </p>
                <p
                  v-if="upNextSong.author"
                  class="text-xs mt-0.5 opacity-70 break-words"
                  :class="bar.theme === 'light' ? 'text-gray-700' : 'text-gray-300'"
                >
                  {{ upNextSong.author }}
                </p>
              </div>
            </div>
          </Transition>

          <!-- ── Karaoke: Currently Performing ── -->
          <Transition name="led-section">
            <div
              v-if="bar.sections.currentlyPlaying && karaokeNowPlaying && isKaraoke"
              class="led-section led-now-playing px-2 py-4"
              :class="
                bar.theme === 'light'
                  ? 'bg-white border-t border-gray-200'
                  : 'bg-gray-950 border-t border-gray-800'
              "
            >
              <div
                class="rounded-xl border-2 px-3 py-2"
                :class="
                  bar.theme === 'light'
                    ? 'border-green-400 bg-green-50/50'
                    : 'border-green-500 bg-green-900/20'
                "
              >
                <span
                  class="text-md uppercase tracking-widest font-semibold mb-1 block"
                  :class="bar.theme === 'light' ? 'text-green-700' : 'text-green-400'"
                  >{{ tForLocale(bar.language, "event.currentlyPlayingBadge") }}</span
                >
                <p
                  class="text-[20px] font-bold font-family-oswald leading-tight break-words text-md"
                  :class="bar.theme === 'light' ? 'text-gray-900' : 'text-white'"
                >
                  {{ karaokeNowPlaying.song?.title }}
                </p>
                <p
                  v-if="karaokeNowPlaying.song?.author"
                  class="text-sm mt-0.5 opacity-80 break-words"
                  :class="bar.theme === 'light' ? 'text-gray-700' : 'text-gray-300'"
                >
                  {{ karaokeNowPlaying.song.author }}
                </p>
                <span
                  v-if="karaokeNowPlaying.performerName"
                  class="text-[14px] font-led-performer-name font-heading tracking-wide flex items-center gap-0.5 mt-1.5"
                  :class="bar.theme === 'light' ? 'text-green-700' : 'text-green-200'"
                >
                  <i class="pi pi-user shrink-0" style="font-size: 16px; padding-right: 10px"></i>
                  <span class="text-[20px] font-semibold uppercase">{{ karaokeNowPlaying.performerName }}</span>
                </span>
              </div>
            </div>
          </Transition>

          <!-- ── Ranked list ── -->
          <div
            v-if="bar.sections.rankedList"
            class="led-section led-ranked py-2 flex-1 flex flex-col overflow-hidden"
            :class="
              bar.theme === 'light'
                ? 'bg-white border-t border-gray-200'
                : 'bg-gray-900 border-t border-gray-700'
            "
          >
            <span
              class="text-md uppercase tracking-widest font-semibold px-3 pt-2 pb-1 flex-shrink-0 block"
              :class="bar.theme === 'light' ? 'text-gray-500' : 'text-gray-200'"
              >{{
                isKaraoke
                  ? tForLocale(bar.language, "event.queueTab")
                  : tForLocale(bar.language, "event.topVotes")
              }}</span
            >
            <div class="flex-1 overflow-y-auto px-3 pb-2">
              <TransitionGroup v-if="!isKaraoke" name="led-song" tag="div">
                <!-- Set-end divider -->
                <div v-for="item in ledRankedItems" :key="item.id">
                  <div v-if="item.type === 'divider'" class="flex items-center gap-1 py-1">
                    <div
                      class="led-set-end-line flex-1 h-px"
                      :class="
                        bar.theme === 'light' ? 'led-set-end-line--light' : 'led-set-end-line--dark'
                      "
                    ></div>
                    <span
                      class="text-[12px] uppercase tracking-wider whitespace-nowrap cs-divider-text"
                      >{{ tForLocale(bar.language, "event.setEndLineShort") }}</span
                    >
                    <div
                      class="led-set-end-line flex-1 h-px"
                      :class="
                        bar.theme === 'light' ? 'led-set-end-line--light' : 'led-set-end-line--dark'
                      "
                    ></div>
                  </div>
                  <div
                    v-else
                    class="led-song-item flex items-center gap-2 py-2 border-b"
                    :class="bar.theme === 'light' ? 'border-gray-100' : 'border-gray-800'"
                  >
                    <!-- Rank badge -->
                    <span
                      class="flex-shrink-0 w-5 h-5 rounded-full text-white text-xs flex items-center justify-center font-bold"
                      :class="concertsStyleStore.ledRankBadgeClass(item.index)"
                      >{{ item.index + 1 }}</span
                    >
                    <!-- Title -->
                    <span
                      class="flex-1 text-md font-medium leading-tight truncate"
                      :class="bar.theme === 'light' ? 'text-gray-900' : 'text-white'"
                      >{{ item.song.title }}</span
                    >
                    <!-- Vote count -->
                    <span class="flex-shrink-0 text-md font-bold tabular-nums cs-vote-count">{{
                      item.song.voteCount
                    }}</span>
                  </div>
                </div>
              </TransitionGroup>
              <TransitionGroup v-else name="led-song" tag="div">
                <div
                  v-for="item in ledKaraokeQueueItems.filter(i => !i.isNowPlaying)"
                  :key="item.id"
                  class="led-song-item flex items-center gap-2 py-2 border-b"
                  :class="bar.theme === 'light' ? 'border-gray-100' : 'border-gray-800'"
                >
                  <span
                    class="flex-shrink-0 w-5 h-5 rounded-full text-white text-sm flex items-center justify-center font-bold"
                    :class="concertsStyleStore.ledRankBadgeClass(item.queueNumber - 1)"
                  >
                    <span>{{ item.queueNumber }}</span>
                  </span>
                  <div class="flex-1 min-w-0">
                    <span
                      class="text-[20px] font-family-oswald font-bold leading-tight truncate block"
                      :class="bar.theme === 'light' ? 'text-gray-900' : 'text-white'"
                      >{{ item.title }}</span
                    >
                    <span
                      v-if="item.performerName"
                      class="text-[20px] font-heading tracking-wide font-bold strong truncate flex items-center gap-0.5 opacity-80"
                      :class="bar.theme === 'light' ? 'text-gray-700' : 'text-gray-100'"
                    >
                      <i class="pi pi-user shrink-0" style="font-size: 16px; padding-right: 4px"></i>
                      {{ item.performerName }}
                    </span>
                  </div>
                </div>
              </TransitionGroup>
              <p
                v-if="isKaraoke ? ledKaraokeQueueItems.filter(i => !i.isNowPlaying).length === 0 : allSongs.length === 0"
                class="text-xs opacity-40 italic pt-1"
                :class="bar.theme === 'light' ? 'text-gray-500' : 'text-gray-400'"
              >
                {{
                  isKaraoke
                    ? tForLocale(bar.language, "event.queueEmpty")
                    : tForLocale(bar.language, "event.noVotesYet")
                }}
              </p>
            </div>
          </div>
        </div>

        <!-- Empty state when no bars added -->
        <div v-if="bars.length === 0" class="flex items-center justify-center flex-1 h-screen">
          <div class="text-center text-gray-500">
            <i class="pi pi-desktop text-5xl mb-4 block"></i>
            <p class="text-lg font-medium">No LED bars configured</p>
            <p class="text-sm mt-1">Click <strong>+ Add Bar</strong> in the panel on the right</p>
          </div>
        </div>
      </template>
    </div>

    <!-- ══════════════════════════════════════════
         RIGHT — Configuration panel
         ══════════════════════════════════════════ -->
    <div
      class="config-panel w-80 flex-shrink-0 bg-gray-900 border-l border-gray-700 overflow-y-auto flex flex-col"
    >
      <!-- Header -->
      <div class="px-5 py-4 border-b border-gray-700 flex items-center justify-between">
        <div>
          <h2 class="text-sm font-bold uppercase tracking-wider text-gray-200">
            LED Configuration
          </h2>
          <p v-if="event" class="text-xs text-gray-400 mt-0.5 truncate">{{ event.name }}</p>
        </div>
        <div class="flex items-center gap-1.5">
          <span
            class="w-2 h-2 rounded-full"
            :class="socketStore.isConnected ? 'bg-green-400' : 'bg-red-400'"
            :title="socketStore.isConnected ? 'Live' : 'Disconnected'"
          ></span>
          <span
            class="text-xs"
            :class="socketStore.isConnected ? 'text-green-400' : 'text-red-400'"
          >
            {{ socketStore.isConnected ? "Live" : "Offline" }}
          </span>
        </div>
      </div>

      <!-- Bar list -->
      <div class="flex-1 px-4 py-4 space-y-5 overflow-y-auto">
        <div
          v-for="(bar, barIndex) in bars"
          :key="bar.id"
          class="rounded-lg border border-gray-700 bg-gray-800 overflow-hidden"
        >
          <!-- Bar header -->
          <div
            class="flex items-center justify-between px-4 py-2.5 bg-gray-750 border-b border-gray-700"
          >
            <span class="text-sm font-semibold text-gray-200">Bar {{ barIndex + 1 }}</span>
            <button
              class="text-gray-500 hover:text-red-400 transition-colors text-xs flex items-center gap-1"
              title="Remove this bar"
              @click="removeBar(bar.id)"
            >
              <i class="pi pi-trash"></i>
              Remove
            </button>
          </div>

          <div class="p-4 space-y-4">
            <!-- Width -->
            <div>
              <label class="block text-xs font-medium text-gray-400 mb-2 uppercase tracking-wider"
                >Width (px)</label
              >
              <div class="flex gap-2 flex-wrap">
                <button
                  v-for="preset in widthPresets"
                  :key="preset"
                  class="px-3 py-1.5 rounded text-xs font-semibold transition-colors"
                  :class="
                    bar.width === preset
                      ? 'bg-teal-500 text-white'
                      : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                  "
                  @click="
                    bar.width = preset;
                    saveConfig();
                  "
                >
                  {{ preset }}px
                </button>
              </div>
              <div class="mt-2 flex items-center gap-2">
                <input
                  v-model.number="bar.width"
                  type="number"
                  min="100"
                  max="1920"
                  class="w-24 bg-gray-700 border border-gray-600 rounded px-2 py-1 text-xs text-gray-200 focus:outline-none focus:border-teal-500"
                  placeholder="custom"
                  @change="saveConfig()"
                />
                <span class="text-xs text-gray-500">custom</span>
              </div>
            </div>

            <!-- Theme -->
            <div>
              <label class="block text-xs font-medium text-gray-400 mb-2 uppercase tracking-wider"
                >Theme</label
              >
              <div class="flex gap-2">
                <button
                  class="flex-1 px-3 py-1.5 rounded text-xs font-semibold transition-colors flex items-center justify-center gap-1.5"
                  :class="
                    bar.theme === 'dark'
                      ? 'bg-gray-600 text-white ring-1 ring-teal-400'
                      : 'bg-gray-700 text-gray-400 hover:bg-gray-600'
                  "
                  @click="
                    bar.theme = 'dark';
                    saveConfig();
                  "
                >
                  <i class="pi pi-moon"></i> Dark
                </button>
                <button
                  class="flex-1 px-3 py-1.5 rounded text-xs font-semibold transition-colors flex items-center justify-center gap-1.5"
                  :class="
                    bar.theme === 'light'
                      ? 'bg-white text-gray-900 ring-1 ring-teal-400'
                      : 'bg-gray-700 text-gray-400 hover:bg-gray-600'
                  "
                  @click="
                    bar.theme = 'light';
                    saveConfig();
                  "
                >
                  <i class="pi pi-sun"></i> Light
                </button>
              </div>
            </div>

            <!-- Language -->
            <div>
              <label class="block text-xs font-medium text-gray-400 mb-2 uppercase tracking-wider"
                >Language</label
              >
              <div class="flex gap-2">
                <button
                  v-for="lang in languageOptions"
                  :key="lang.value"
                  class="flex-1 flex items-center justify-center gap-1.5 px-3 py-1.5 rounded text-xs font-semibold transition-colors"
                  :class="
                    bar.language === lang.value
                      ? 'bg-teal-600 text-white ring-1 ring-teal-400'
                      : 'bg-gray-700 text-gray-400 hover:bg-gray-600'
                  "
                  @click="
                    bar.language = lang.value;
                    saveConfig();
                  "
                >
                  <img :src="lang.flag" :alt="lang.label" class="led-flag-icon" />
                  {{ lang.label }}
                </button>
              </div>
            </div>

            <!-- Sections -->
            <div>
              <label class="block text-xs font-medium text-gray-400 mb-2 uppercase tracking-wider"
                >Sections</label
              >
              <div class="space-y-2">
                <!-- Logo: always on, not toggleable -->
                <div class="flex items-center gap-2 opacity-50 cursor-default select-none">
                  <div
                    class="w-4 h-4 rounded bg-teal-500 flex items-center justify-center flex-shrink-0"
                  >
                    <i class="pi pi-check text-white" style="font-size: 9px"></i>
                  </div>
                  <span class="text-xs text-gray-400"
                    >Logo <span class="text-gray-600">(always shown)</span></span
                  >
                </div>

                <!-- Toggleable sections -->
                <div
                  v-for="section in toggleableSections"
                  :key="section.key"
                  class="flex items-center gap-2 cursor-pointer group"
                  @click="toggleSection(bar, section.key)"
                >
                  <div
                    class="w-4 h-4 rounded flex-shrink-0 border transition-colors flex items-center justify-center"
                    :class="
                      bar.sections[section.key]
                        ? 'bg-teal-500 border-teal-500'
                        : 'bg-transparent border-gray-600 group-hover:border-gray-400'
                    "
                  >
                    <i
                      v-if="bar.sections[section.key]"
                      class="pi pi-check text-white"
                      style="font-size: 9px"
                    ></i>
                  </div>
                  <span
                    class="text-xs transition-colors"
                    :class="
                      bar.sections[section.key]
                        ? 'text-gray-200'
                        : 'text-gray-500 group-hover:text-gray-400'
                    "
                    >{{ section.label }}</span
                  >
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Add bar button -->
      <div class="px-4 py-4 border-t border-gray-700">
        <button
          class="w-full flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg bg-teal-600 hover:bg-teal-500 text-white text-sm font-semibold transition-colors"
          @click="addBar"
        >
          <i class="pi pi-plus"></i>
          Add Bar
        </button>
        <p class="text-center text-xs text-gray-600 mt-2">Config auto-saved to browser storage</p>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, watch, onMounted, onUnmounted } from "vue";
import { useRoute } from "vue-router";
import QRCode from "qrcode";
import { useI18n } from "vue-i18n";
import api from "../services/api";
import { useVotesStore } from "../stores/votes";
import { useSocketStore } from "../stores/socket";
import { useConcertsStyleStore } from "../stores/concertsStyle";
import { useEventPageStore } from "../stores/eventPage";
import { useLanguageStore } from "../stores/language";
import { resolveEventLocales, resolveLandingLocale } from "../constants/locales";
import { buildEventPublicUrl } from "../utils/eventUrl";
import { computeSetEndIndex, buildSongLengthMap } from "../utils/setlistCalculator";
import { filterSongsForEvent } from "../utils/eventSongFilter";
import { isKaraokeEvent } from "../constants/eventTypes";
import { DEFAULT_LOCALE } from "@/utils/localeUtils.js";
import { languagePickerOptions } from "../i18n/languageOptions.js";
import { translateForLocale } from "../i18n/messages.js";

// ──────────────────────────────────────────────────────────
// Route & stores
// ──────────────────────────────────────────────────────────
const route = useRoute();
const { t } = useI18n();
const votesStore = useVotesStore();
const socketStore = useSocketStore();
const concertsStyleStore = useConcertsStyleStore();
const eventPageStore = useEventPageStore();
eventPageStore.setLoading(true);
const languageStore = useLanguageStore();

const languageOptions = computed(() => {
  const enabled = eventPageStore.enabledLocales;
  if (enabled.length === 0) {
    return languagePickerOptions;
  }
  return languagePickerOptions.filter((option) => enabled.includes(option.value));
});
const tForLocale = (locale, key) => translateForLocale(locale, key, DEFAULT_LOCALE);

// ──────────────────────────────────────────────────────────
// Event / songs state
// ──────────────────────────────────────────────────────────
const event = ref(null);
const songs = ref([]);
const error = ref(null);

const eventUrl = computed(() => {
  if (!event.value?.slug) return "";
  return buildEventPublicUrl(
    event.value.slug,
    resolveEventLocales(event.value.enabledLocales, event.value.defaultLocale).defaultLocale
  );
});

const qrCodeDataUrl = ref("");

watch(
  eventUrl,
  async (url) => {
    if (!url) {
      qrCodeDataUrl.value = "";
      return;
    }
    try {
      qrCodeDataUrl.value = await QRCode.toDataURL(url, {
        width: 280,
        margin: 1,
        color: { dark: "#000000", light: "#FFFFFF" }
      });
    } catch {
      qrCodeDataUrl.value = "";
    }
  },
  { immediate: true }
);

// ──────────────────────────────────────────────────────────
// Currently playing & up-next
// ──────────────────────────────────────────────────────────
const isKaraoke = computed(() => isKaraokeEvent(event.value));

const karaokeNowPlaying = computed(() => {
  if (!isKaraoke.value || !event.value) return null;
  return votesStore.getNowPlaying(event.value.id);
});

const karaokeQueueEntries = computed(() => {
  if (!isKaraoke.value || !event.value) return [];
  return votesStore.getQueueEntries(event.value.id);
});

const latestPerformedSong = computed(() => {
  if (!event.value) return null;
  if (isKaraoke.value) {
    const playing = karaokeNowPlaying.value;
    if (!playing?.song) return null;
    return { ...playing.song, performerName: playing.performerName || "" };
  }
  const eventVotes = votesStore.getEventVotes(event.value.id);
  const performed = eventVotes
    .filter((v) => v.isPerformed && v.performedAt && !v.endedAt)
    .sort((a, b) => new Date(b.performedAt) - new Date(a.performedAt));
  if (!performed.length) return null;
  const song = songs.value.find((s) => s.id === performed[0].songId);
  return song ? { ...song } : null;
});

const upNextSong = computed(() => {
  if (!event.value) return null;
  const upNextId = votesStore.getUpNextSongId(event.value.id);
  if (!upNextId) return null;
  return songs.value.find((s) => s.id === upNextId) || null;
});

// ──────────────────────────────────────────────────────────
// Ranked list
// ──────────────────────────────────────────────────────────
const sortedResults = computed(() => {
  if (!event.value) return [];
  return votesStore
    .getEventVotes(event.value.id)
    .filter((v) => v.totalVotes > 0 && !v.isPerformed)
    .map((v) => {
      const song = songs.value.find((s) => s.id === (v.songId || v.song?.id));
      return {
        id: v.songId || v.song?.id,
        title: v.song?.title || song?.title || "Unknown",
        author: v.song?.author || song?.author || "",
        voteCount: v.totalVotes
      };
    });
});

const allSongs = computed(() => {
  const upNextId = upNextSong.value?.id;
  return sortedResults.value.filter((s) => s.id !== upNextId);
});

// ── Setlist set-end marker ──────────────────────────────────────────────────
const setlistSettings = ref(null);

const ledSetEndLineIndex = computed(() => {
  const ev = event.value;
  if (!ev || !ev.setlistEnabled || !ev.currentSetStartedAt || !setlistSettings.value) return -1;
  const durations = Array.isArray(ev.setlistDurations) ? ev.setlistDurations : [];
  const setIdx = ev.currentSetIndex || 0;
  if (setIdx >= durations.length) return -1;
  const songLengthMap = buildSongLengthMap([
    ...allSongs.value,
    ...(upNextSong.value ? [upNextSong.value] : [])
  ]);
  return computeSetEndIndex(allSongs.value, {
    setDurationMinutes: durations[setIdx],
    pauseTimeSec: setlistSettings.value.pauseTime || 30,
    defaultSongTimeSec: setlistSettings.value.defaultSongTime || 240,
    upNextSong: upNextSong.value,
    currentSong: latestPerformedSong.value,
    setStartedAt: ev.currentSetStartedAt,
    songLengthMap
  });
});

// Flat list merging song rows and the set-end divider for TransitionGroup
const ledRankedItems = computed(() => {
  const items = [];
  const dividerIdx = ledSetEndLineIndex.value;
  allSongs.value.forEach((song, index) => {
    if (index === dividerIdx) {
      items.push({ type: "divider", id: "__set-end-divider__" });
    }
    items.push({ type: "song", id: song.id, song, index });
  });
  return items;
});

const ledKaraokeQueueItems = computed(() => {
  const items = [];
  const playing = karaokeNowPlaying.value;

  if (playing) {
    items.push({
      id: `playing-${playing.performedSongId || playing.songId}`,
      title: playing.song?.title || "Unknown",
      performerName: playing.performerName || "",
      queueNumber: 0,
      isNowPlaying: true
    });
  }

  karaokeQueueEntries.value.forEach((entry, index) => {
    items.push({
      id: `vote-${entry.voteId}`,
      title: entry.song?.title || "Unknown",
      performerName: entry.performerName || "",
      queueNumber: index + 1,
      isNowPlaying: false
    });
  });

  return items;
});

// ──────────────────────────────────────────────────────────
// Bar configuration
// ──────────────────────────────────────────────────────────
const widthPresets = [276, 344];

const toggleableSections = [
  { key: "eventName", label: "Event name" },
  { key: "qrCode", label: "QR code" },
  { key: "currentlyPlaying", label: "Currently Playing" },
  { key: "upNext", label: "Up Next" },
  { key: "rankedList", label: "Ranked list" }
];

const defaultBar = () => {
  const resolved = resolveEventLocales(event.value?.enabledLocales, event.value?.defaultLocale);
  return {
    id: Date.now() + Math.random(),
    width: 276,
    theme: "dark",
    language: resolved.defaultLocale,
    sections: {
      eventName: true,
      qrCode: true,
      currentlyPlaying: true,
      upNext: true,
      rankedList: true
    }
  };
};

const storageKey = computed(() => `led-bars-${route.params.slug}`);

const loadConfig = () => {
  try {
    const raw = localStorage.getItem(storageKey.value);
    if (raw) return JSON.parse(raw);
  } catch {
    /* ignore */
  }
  return null;
};

const saveConfig = () => {
  try {
    localStorage.setItem(storageKey.value, JSON.stringify(bars.value));
  } catch {
    /* ignore */
  }
};

const initialConfig = loadConfig();
const bars = ref(initialConfig || [defaultBar()]);

const addBar = () => {
  bars.value.push(defaultBar());
  saveConfig();
};

const removeBar = (id) => {
  bars.value = bars.value.filter((b) => b.id !== id);
  saveConfig();
};

const toggleSection = (bar, key) => {
  bar.sections[key] = !bar.sections[key];
  saveConfig();
};

// ──────────────────────────────────────────────────────────
// Data fetching
// ──────────────────────────────────────────────────────────
const fetchEvent = async () => {
  try {
    eventPageStore.setLoading(true);
    error.value = null;
    const response = await api.get(`/api/events/slug/${route.params.slug}`);
    if (response.data.success) {
      const fetchedEvent = response.data.data.event;
      const fetchedSongs = response.data.data.songs || [];
      const resolvedLocales = resolveEventLocales(
        fetchedEvent.enabledLocales,
        fetchedEvent.defaultLocale
      );

      event.value = {
        ...fetchedEvent,
        enabledLocales: resolvedLocales.enabledLocales,
        defaultLocale: resolvedLocales.defaultLocale
      };
      songs.value = filterSongsForEvent(fetchedSongs, fetchedEvent);
      concertsStyleStore.applyEventStyle(fetchedEvent.concertsStyle);
      eventPageStore.setEventLocales(resolvedLocales.enabledLocales);

      const locale = resolveLandingLocale(
        fetchedEvent.enabledLocales,
        fetchedEvent.defaultLocale,
        route.query.lang
      );
      languageStore.applyEventLocale(locale);

      if (!initialConfig && bars.value.length > 0) {
        bars.value[0].language = resolvedLocales.defaultLocale;
      }

      if (response.data.data.setlistSettings) {
        setlistSettings.value = response.data.data.setlistSettings;
      }
      await votesStore.fetchEventVotes(event.value.id);
      socketStore.connect();
      socketStore.joinEvent(event.value.id);
    } else {
      error.value = "Event not found";
    }
  } catch (err) {
    error.value = err.response?.data?.message || "Failed to load event";
  } finally {
    eventPageStore.setLoading(false);
  }
};

// ──────────────────────────────────────────────────────────
// Socket event listeners (re-use store handlers)
// ──────────────────────────────────────────────────────────
const handleSetChanged = (data) => {
  if (data.eventId === event.value?.id) {
    event.value = {
      ...event.value,
      currentSetIndex: data.currentSetIndex,
      currentSetStartedAt: data.currentSetStartedAt,
      ...(data.setlistDurations !== undefined ? { setlistDurations: data.setlistDurations } : {})
    };
  }
};

const handleSongPerformedWithSetUpdate = async (data) => {
  if (event.value && data.eventId === event.value.id && data.currentSetStartedAt !== undefined) {
    event.value = {
      ...event.value,
      currentSetStartedAt: data.currentSetStartedAt,
      currentSetIndex: data.currentSetIndex ?? event.value.currentSetIndex
    };
  }
  await votesStore.handleSongPerformed(data);
};

onMounted(async () => {
  await fetchEvent();
  if (!socketStore.isConnected) socketStore.connect();
  socketStore.on("vote_updated", votesStore.handleVoteUpdated);
  socketStore.on("song_performed", handleSongPerformedWithSetUpdate);
  socketStore.on("song_ended", votesStore.handleSongEnded);
  socketStore.on("set_changed", handleSetChanged);
  socketStore.on("connect", () => {
    if (event.value) socketStore.joinEvent(event.value.id);
  });
});

onUnmounted(() => {
  socketStore.off("vote_updated", votesStore.handleVoteUpdated);
  socketStore.off("song_performed", handleSongPerformedWithSetUpdate);
  socketStore.off("song_ended", votesStore.handleSongEnded);
  socketStore.off("set_changed", handleSetChanged);
  if (event.value) socketStore.leaveEvent(event.value.id);
  concertsStyleStore.resetToDefault();
  eventPageStore.setLoading(false);
});
</script>

<style scoped>
/* ── Root fills the viewport completely ── */
.led-root {
  position: fixed;
  inset: 0;
  z-index: 9999;
}

/* ── Bar themes ── */
.led-bar--dark {
  background-color: #000;
  color: #fff;
}

.led-bar--light {
  background-color: #f9fafb;
  color: #111827;
}

/* ── Section dividers already handled via border-t on each section ── */

/* ── Config panel bg tweak ── */
.bg-gray-750 {
  background-color: #1f2937; /* between gray-800 and gray-700 */
}

/* ── Flag icons in language selector ── */
.led-flag-icon {
  width: 20px;
  height: 14px;
  object-fit: contain;
  display: block;
  flex-shrink: 0;
}

/* ── Section slide-in / slide-out (Currently Playing, Up Next) ── */
.led-section-enter-active,
.led-section-leave-active {
  transition:
    max-height 0.4s ease,
    opacity 0.4s ease,
    padding-top 0.4s ease,
    padding-bottom 0.4s ease;
  overflow: hidden;
  max-height: 160px; /* tall enough for title + artist */
}

.led-section-enter-from,
.led-section-leave-to {
  max-height: 0;
  opacity: 0;
  padding-top: 0 !important;
  padding-bottom: 0 !important;
}

/* ── Ranked list song rows — fade + slide ── */
.led-song-item {
  transition:
    transform 0.35s ease,
    opacity 0.35s ease;
}

.led-song-enter-active,
.led-song-leave-active {
  transition:
    opacity 0.3s ease,
    max-height 0.3s ease;
  overflow: hidden;
  max-height: 56px;
}

.led-song-enter-from,
.led-song-leave-to {
  opacity: 0;
  max-height: 0;
}

/* FLIP move animation for re-ordering */
.led-song-move {
  transition: transform 0.4s ease;
}

/* ── Set-end divider glow ── */
.led-set-end-line {
  border-top: 1px solid;
}

.led-set-end-line--light {
  border-color: rgb(20 184 166);
  box-shadow: 0px 0px 6px 2px rgba(20, 184, 166, 0.5);
}

.led-set-end-line--dark {
  border-color: rgb(45 212 191);
  box-shadow: 0px 0px 6px 2px rgba(45, 212, 191, 0.5);
}
</style>
